import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { formatScore, getGroupLabel, sortGroupKeys } from '../utils/meetResultsParser';

const EVENT_FIELDS = [
  { key: 'floor', label: 'Floor' },
  { key: 'pommelHorse', label: 'Pommel' },
  { key: 'rings', label: 'Rings' },
  { key: 'vault', label: 'Vault' },
  { key: 'parallelBars', label: 'P-Bars' },
  { key: 'highBar', label: 'High Bar' },
  { key: 'allAround', label: 'All-Around' }
];

const getEventWinners = (athletes, eventKey) => {
  const valid = athletes.filter((athlete) => athlete[eventKey]?.score > 0);
  if (!valid.length) return null;

  const bestScore = Math.max(...valid.map((athlete) => athlete[eventKey].score));
  return {
    score: bestScore,
    winners: valid.filter((athlete) => athlete[eventKey].score === bestScore)
  };
};

const getComputedPlacement = (athletes, eventKey, score) => {
  if (!score || score <= 0) return null;

  const epsilon = 0.0001;
  const eventScores = athletes
    .map((athlete) => athlete[eventKey]?.score || 0)
    .filter((value) => value > 0);

  if (!eventScores.length) return null;

  const higherCount = eventScores.filter((value) => value > score + epsilon).length;
  const tieCount = eventScores.filter((value) => Math.abs(value - score) <= epsilon).length;

  return {
    rank: higherCount + 1,
    isTie: tieCount > 1
  };
};

const renderScoreWithPlacement = (eventResult, placement) => {
  if (!eventResult || eventResult.score <= 0) {
    return <span className="text-sm text-gray-900 dark:text-white">-</span>;
  }

  const placementText = placement?.rank
    ? `${placement.rank}${placement.isTie ? 'T' : ''}`
    : null;

  return (
    <div className="flex flex-col items-center leading-tight">
      <span className="text-sm text-gray-900 dark:text-white">{formatScore(eventResult.score)}</span>
      {placementText && <span className="text-xs text-gray-500 dark:text-gray-400">{placementText}</span>}
    </div>
  );
};

const MeetDetail = () => {
  const { meetId } = useParams();
  const [meet, setMeet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadMeet = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const meetRef = doc(db, 'meets', meetId);
      const meetDoc = await getDoc(meetRef);

      if (!meetDoc.exists()) {
        setError('Meet not found');
        setLoading(false);
        return;
      }

      setMeet({
        id: meetDoc.id,
        ...meetDoc.data()
      });
    } catch (err) {
      console.error('Error loading meet:', err);
      setError('Failed to load meet');
    } finally {
      setLoading(false);
    }
  }, [meetId]);

  useEffect(() => {
    loadMeet();
  }, [loadMeet]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-xl text-gray-600 dark:text-gray-400">Loading meet...</p>
        </div>
      </div>
    );
  }

  if (error || !meet) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-8 max-w-md">
          <p className="text-red-600 dark:text-red-400 text-center mb-4">{error || 'Meet not found'}</p>
          <Link
            to="/meets"
            className="block w-full text-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
          >
            Back to Meets
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Link
            to="/meets"
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to All Meets
          </Link>

          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{meet.name}</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {new Date(meet.date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Meet Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Total Gymnasts</p>
              <p className="text-3xl font-bold text-blue-900 dark:text-blue-300">{meet.results.totalGymnasts}</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <p className="text-sm text-green-600 dark:text-green-400 font-medium">Categories</p>
              <p className="text-3xl font-bold text-green-900 dark:text-green-300">{Object.keys(meet.results.groups).length}</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
              <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">Date</p>
              <p className="text-lg font-bold text-purple-900 dark:text-purple-300">{new Date(meet.date).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {sortGroupKeys(meet.results.groups).map((groupKey) => {
            const group = meet.results.groups[groupKey];
            const groupAthletes = meet.results.allGymnasts.filter(
              (athlete) => athlete.level === group.level && athlete.division === group.division
            );

            return (
              <div key={groupKey} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  {getGroupLabel(group.level, group.division)} - Top 10
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Total Athletes: {group.totalAthletes}</p>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Event Winners</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                    {EVENT_FIELDS.map((eventField) => {
                      const eventResult = getEventWinners(groupAthletes, eventField.key);
                      return (
                        <div
                          key={eventField.key}
                          className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900"
                        >
                          <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">{eventField.label}</p>
                          {!eventResult ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">No score</p>
                          ) : (
                            <>
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                {eventResult.winners.map((winner) => winner.name).join(', ')}
                              </p>
                              <p className="text-xs text-gray-600 dark:text-gray-400">
                                {eventResult.winners.map((winner) => winner.team).join(', ')}
                              </p>
                              <p className="text-sm text-blue-600 dark:text-blue-400 font-bold mt-1">{formatScore(eventResult.score)}</p>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rank</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Gymnast</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Team</th>
                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Floor</th>
                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Pommel</th>
                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rings</th>
                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Vault</th>
                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">P-Bars</th>
                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">High Bar</th>
                        <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">All-Around</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {group.top10.map((athlete, index) => (
                        <tr
                          key={index}
                          className={`${index < 3 ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''} hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors`}
                        >
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {index === 0 && <span className="text-xl mr-1">🥇</span>}
                              {index === 1 && <span className="text-xl mr-1">🥈</span>}
                              {index === 2 && <span className="text-xl mr-1">🥉</span>}
                              <span className="text-sm font-medium text-gray-900 dark:text-white">{index + 1}</span>
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap"><span className="text-sm font-medium text-gray-900 dark:text-white">{athlete.name}</span></td>
                          <td className="px-3 py-4 whitespace-nowrap"><span className="text-sm text-gray-600 dark:text-gray-400">{athlete.team}</span></td>
                          <td className="px-2 py-4 whitespace-nowrap text-center">{renderScoreWithPlacement(athlete.floor, athlete.floor.rank ? { rank: athlete.floor.rank, isTie: athlete.floor.isTie } : getComputedPlacement(groupAthletes, 'floor', athlete.floor.score))}</td>
                          <td className="px-2 py-4 whitespace-nowrap text-center">{renderScoreWithPlacement(athlete.pommelHorse, athlete.pommelHorse.rank ? { rank: athlete.pommelHorse.rank, isTie: athlete.pommelHorse.isTie } : getComputedPlacement(groupAthletes, 'pommelHorse', athlete.pommelHorse.score))}</td>
                          <td className="px-2 py-4 whitespace-nowrap text-center">{renderScoreWithPlacement(athlete.rings, athlete.rings.rank ? { rank: athlete.rings.rank, isTie: athlete.rings.isTie } : getComputedPlacement(groupAthletes, 'rings', athlete.rings.score))}</td>
                          <td className="px-2 py-4 whitespace-nowrap text-center">{renderScoreWithPlacement(athlete.vault, athlete.vault.rank ? { rank: athlete.vault.rank, isTie: athlete.vault.isTie } : getComputedPlacement(groupAthletes, 'vault', athlete.vault.score))}</td>
                          <td className="px-2 py-4 whitespace-nowrap text-center">{renderScoreWithPlacement(athlete.parallelBars, athlete.parallelBars.rank ? { rank: athlete.parallelBars.rank, isTie: athlete.parallelBars.isTie } : getComputedPlacement(groupAthletes, 'parallelBars', athlete.parallelBars.score))}</td>
                          <td className="px-2 py-4 whitespace-nowrap text-center">{renderScoreWithPlacement(athlete.highBar, athlete.highBar.rank ? { rank: athlete.highBar.rank, isTie: athlete.highBar.isTie } : getComputedPlacement(groupAthletes, 'highBar', athlete.highBar.score))}</td>
                          <td className="px-3 py-4 whitespace-nowrap text-center">
                            <div className="flex flex-col items-center leading-tight">
                              <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{formatScore(athlete.allAround.score)}</span>
                              {(athlete.allAround.rank || getComputedPlacement(groupAthletes, 'allAround', athlete.allAround.score)?.rank) && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {athlete.allAround.rank || getComputedPlacement(groupAthletes, 'allAround', athlete.allAround.score)?.rank}
                                  {(athlete.allAround.rank ? athlete.allAround.isTie : getComputedPlacement(groupAthletes, 'allAround', athlete.allAround.score)?.isTie) ? 'T' : ''}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MeetDetail;
